import { useCallback, useMemo } from "react";

import { useConfiguredAgents } from "@/api/agentConfig";
import { useCustomerAgents } from "@/api/customers";
import { useOrgChart } from "@/api/org";
import { humanizeSlug } from "@/lib/format";
import { hueForKey } from "@/lib/identity";
import { useAuth } from "@/stores/auth";

// Who the agents are, by template: the single place that turns
// "assistant" into "Julia" (and "Assistant" as her role).
//
// Name precedence: the configured name (rename-fresh, what the org
// editor writes) > the manifest display_name (lags a rename until the
// next deploy) > the humanised template. A role title ("Legal Counsel")
// is NEVER used as a name - it is exposed separately as `role`.
//
// Special values in person fields: "" and "operator" are the operator
// ("You"), "*" is everyone; null/undefined (nobody) give "".
//
// A child login (role "family") may only read /agents/configured; the
// manifest and org chart are operator-only, so they are not requested.

export interface AgentEntry {
  template: string;
  name: string;
  /** Role label from the org chart ("Assistant", "Legal Counsel"), else the blueprint. */
  role: string | null;
  roleDescription: string | null;
  avatarUrl: string | null;
  /** "subagent" = a spawn-only blueprint (no channel, desktop or mail). */
  kind: "standard" | "family" | "subagent";
  /** Identity hue 1…8 for avatars and chips: hueForKey(template), the same for every viewer. */
  hue: number;
}

export interface AgentDirectory {
  agents: AgentEntry[];
  byTemplate: Map<string, AgentEntry>;
  /** Display name for a template; "You" for ""/"operator", "Everyone" for "*", "" for null. */
  nameOf: (template: string | null | undefined) => string;
  roleOf: (template: string | null | undefined) => string | null;
  hueOf: (template: string | null | undefined) => number;
  isLoading: boolean;
  /** Whether an agent list has answered (an error, or still loading, is not an answer). */
  answered: boolean;
}

const SELF = new Set(["", "operator"]);

// An agent's name is a proper noun: "juan-jr" → "Juan Jr".
const titleName = (template: string) => humanizeSlug(template, null, { titleCase: true });

export function useAgentDirectory(customerId: string | null | undefined): AgentDirectory {
  const isChild = useAuth((s) => s.role) === "family";
  const id = customerId || null;
  const configured = useConfiguredAgents(id);
  const manifest = useCustomerAgents(isChild ? null : id);
  const org = useOrgChart(isChild ? null : id);

  const agents = useMemo<AgentEntry[]>(() => {
    const cfg = new Map((configured.data?.agents ?? []).map((a) => [a.template, a]));
    const roles = new Map((org.data?.roles ?? []).map((r) => [r.template, r]));
    const listed = manifest.data?.agents ?? [];
    // Manifest order first (new agents are appended there), then any
    // configured agent the manifest has not caught up with yet.
    const templates = [...listed.map((a) => a.template), ...[...cfg.keys()].filter((t) => !listed.some((a) => a.template === t))];
    const byManifest = new Map(listed.map((a) => [a.template, a]));
    return templates.map((template) => {
      const c = cfg.get(template);
      const m = byManifest.get(template);
      const r = roles.get(template);
      const name = c?.name?.trim() || m?.display_name?.trim() || titleName(template);
      const role = r?.title?.trim() || (c?.blueprint ? humanizeSlug(c.blueprint) : "") || null;
      return {
        template,
        name,
        role: role && role !== name ? role : null,
        roleDescription: r?.description?.trim() || null,
        avatarUrl: m?.avatar_url ?? r?.avatar_url ?? null,
        kind: c?.kind ?? m?.kind ?? "standard",
        hue: hueForKey(template),
      };
    });
  }, [configured.data, manifest.data, org.data]);

  const byTemplate = useMemo(() => new Map(agents.map((a) => [a.template, a])), [agents]);

  const nameOf = useCallback(
    (template: string | null | undefined) => {
      if (template === null || template === undefined) return "";
      const t = template.trim();
      if (SELF.has(t.toLowerCase())) return "You";
      if (t === "*") return "Everyone";
      return byTemplate.get(t)?.name ?? titleName(t);
    },
    [byTemplate],
  );
  const roleOf = useCallback((template: string | null | undefined) => byTemplate.get((template ?? "").trim())?.role ?? null, [byTemplate]);
  const hueOf = useCallback((template: string | null | undefined) => hueForKey(template), []);

  // Loading only until the first source that can answer has answered;
  // an operator-only source that errors must not keep callers waiting.
  const isLoading = configured.isLoading && (isChild || (manifest.isLoading && !manifest.data));
  const answered = configured.data !== undefined || manifest.data !== undefined;

  return { agents, byTemplate, nameOf, roleOf, hueOf, isLoading, answered };
}
